import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { KeyRound } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { Button, Field, InlineAlert, Input, Spinner } from '@aeris/ui'

import { AuthCard } from '../components/auth-card.tsx'
import { ApiError, ApiUnreachableError, changePassword } from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'

/**
 * Change password.
 *
 * Two audiences, one screen. Somebody who chose "Trocar senha" from the menu,
 * and somebody who was sent here because an administrator generated the
 * password they are holding — the second is not free to leave, and the copy
 * says so rather than showing a Cancel button that does nothing.
 *
 * Outside the application shell on purpose. A sidebar offering Produtos and
 * Equipe to somebody whose every request will be refused is an interface
 * lying about what is available.
 *
 * The rules are the server's. This form checks that the fields are filled and
 * that the two new entries match — the second is a typing check, not a policy —
 * and lets the API answer everything else. Duplicating the length rule here
 * would be a second place to update it.
 */

const schema = z
  .object({
    currentPassword: z.string().min(1, 'Informe a senha atual').max(256),
    newPassword: z.string().min(1, 'Informe a nova senha').max(256),
    confirmation: z.string().min(1, 'Repita a nova senha').max(256),
  })
  .refine((values) => values.newPassword === values.confirmation, {
    path: ['confirmation'],
    message: 'As senhas não conferem',
  })

type FormValues = z.infer<typeof schema>

const messageFor = (error: unknown): string => {
  if (error instanceof ApiUnreachableError) {
    return 'Não foi possível falar com o servidor. Verifique sua conexão.'
  }
  if (error instanceof ApiError) return error.message
  return 'Não foi possível trocar a senha. Tente novamente.'
}

export const TrocarSenhaPage = (): ReactNode => {
  const { session, isLoading, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [formError, setFormError] = useState<string | null>(null)
  const [changed, setChanged] = useState(false)

  const required = session?.user.mustChangePassword ?? false

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      void navigate({ to: '/login', replace: true })
    }
  }, [isAuthenticated, isLoading, navigate])

  /**
   * Leaving is driven by the refreshed session, not by the submit handler.
   *
   * The obligation is the server's to lift, so the moment to move on is the
   * moment `/auth/me` stops reporting it — not the moment the request returned.
   * Navigating straight from the handler races the refetch: the shell's guard
   * would read a session that still says a change is due and send the user
   * back here, which is a redirect loop that only shows up under load.
   */
  useEffect(() => {
    if (changed && !required) {
      void navigate({ to: '/inicio', replace: true })
    }
  }, [changed, required, navigate])

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { currentPassword: '', newPassword: '', confirmation: '' },
  })

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null)
    try {
      await changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      })
      setChanged(true)
      /**
       * Refetched rather than patched locally.
       *
       * `mustChangePassword` is the server's answer, and the whole point of
       * this screen is that the server decides when the obligation is
       * discharged. Setting the flag to false in the browser would be the
       * client marking its own homework.
       */
      await queryClient.invalidateQueries({ queryKey: ['session'] })
    } catch (error) {
      setFormError(messageFor(error))
    }
  })

  if (isLoading) {
    return (
      <div
        className="flex h-[100dvh] items-center justify-center bg-(--sidebar-bg) text-(--sidebar-text)"
        role="status"
        aria-live="polite"
      >
        <Spinner />
        <span className="sr-only">Verificando sessão…</span>
      </div>
    )
  }

  return (
    <AuthCard
      footer={
        /*
         * Voltar só existe para quem ESCOLHEU trocar (F132). Quem foi mandado
         * para cá com a senha que o administrador gerou não está livre para
         * sair, e um botão que não leva a lugar nenhum seria pior do que não
         * ter botão. Antes, as duas pessoas ficavam sem saída na tela.
         */
        required ? undefined : (
          <Button asChild variant="link" className="text-(--sidebar-text)">
            <Link to="/inicio">Voltar ao Aeris sem trocar</Link>
          </Button>
        )
      }
    >
      <form
        onSubmit={(event) => {
          void onSubmit(event)
        }}
        noValidate
        className="flex flex-col gap-4"
      >
        <div className="flex flex-col gap-1">
          <h1 className="text-(length:--text-lg) font-(--weight-semibold) text-(--text-primary)">
            {required ? 'Defina sua senha' : 'Trocar senha'}
          </h1>
          <p className="text-(length:--text-xs) text-(--text-muted)">
            {required
              ? 'Sua senha atual foi criada por um administrador. Escolha uma senha que só você conheça para continuar.'
              : 'As outras sessões desta conta serão encerradas.'}
          </p>
        </div>

        {formError === null ? null : (
          <InlineAlert tone="danger">{formError}</InlineAlert>
        )}

        <Field
          label={required ? 'Senha temporária' : 'Senha atual'}
          required
          error={errors.currentPassword?.message}
        >
          {(field) => (
            <Input
              {...field}
              {...register('currentPassword')}
              type="password"
              autoFocus
              autoComplete="current-password"
            />
          )}
        </Field>

        <Field
          label="Nova senha"
          required
          error={errors.newPassword?.message}
          hint="Pelo menos 12 caracteres"
        >
          {(field) => (
            <Input
              {...field}
              {...register('newPassword')}
              type="password"
              autoComplete="new-password"
            />
          )}
        </Field>

        <Field
          label="Repita a nova senha"
          required
          error={errors.confirmation?.message}
        >
          {(field) => (
            <Input
              {...field}
              {...register('confirmation')}
              type="password"
              autoComplete="new-password"
            />
          )}
        </Field>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={isSubmitting}
          leadingIcon={<KeyRound aria-hidden="true" className="h-4 w-4" />}
          className="mt-1 w-full"
        >
          Salvar nova senha
        </Button>
      </form>
    </AuthCard>
  )
}
